import { useMutation } from "@apollo/client/react";
import {
  AdminMultipleChoiceQuestionOptionFragment,
  DeleteMultipleChoiceQuestionOption,
  DeleteMultipleChoiceQuestionOptionVariables,
  MultipleChoiceType,
  UpdateMultipleChoiceQuestionOption,
  UpdateMultipleChoiceQuestionOptionVariables,
} from "__generated__/types";
import barsicon from "common/assets/font-awesome/bars-solid.svg";
import deleteIcon from "common/assets/font-awesome/delete.svg";
import { IconButton } from "common/buttons";
import { useConfirmDialog } from "common/components/confirm-dialog/use-confirm-dialog";
import { FieldError } from "common/components/forms/FieldError";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { InputField } from "common/components/InputField";
import { ListItem } from "common/components/lists/ListItem";
import { SimpleErrorMessage } from "common/components/SimpleErrorMessage";
import { useDebounce } from "common/hooks/useDebounce";
import {
  DnDItemType,
  useDragAndDropElement,
} from "common/hooks/useDragAndDropElement";
import { preventDragOnTextInput } from "common/utils/element-utils";
import gql from "graphql-tag";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import optionStyles from "./QuestionListOptionItem.module.css";

const MUTATION = gql`
  mutation UpdateMultipleChoiceQuestionOption(
    $input: UpdateMultipleChoiceQuestionOptionInput!
  ) {
    updateMultipleChoiceQuestionOption(input: $input) {
      option {
        id
        title
        correct
      }
    }
  }
`;

const DELETE_OPTION = gql`
  mutation DeleteMultipleChoiceQuestionOption(
    $input: DeleteMultipleChoiceQuestionOptionInput!
  ) {
    deleteMultipleChoiceQuestionOption(input: $input) {
      question {
        id
        title
      }
    }
  }
`;

interface Props {
  option: AdminMultipleChoiceQuestionOptionFragment;
  multipleChoiceId: string;
  questionId: string;
  numberOfChecked: number;
  type: MultipleChoiceType;
  index: number;
  moveElement(dragIndex: number, hoverIndex: number): void;
  saveOrder(dragIndex: number, dropIndex: number, id: string): void;
  refetch(): void;
}

type UpdateMultipleChoiceQuestionOptionForm = Record<string, string> & {
  multipleChoiceId: string;
  multipleChoiceQuestionId: string;
  correct: boolean;
};

function parseCorrect(value: boolean | null): boolean {
  if (value == null) {
    throw new Error("Missing correct details");
  }
  return value;
}

export const QuestionListOptionItem: React.FC<
  React.PropsWithChildren<Props>
> = ({
  option,
  multipleChoiceId,
  questionId,
  numberOfChecked,
  type,
  index,
  moveElement,
  saveOrder,
  refetch,
}: Props) => {
  const [hasNumberOfCheckedError, setHasNumberOfCheckedError] = useState(false);

  useEffect(() => {
    if (numberOfChecked > 1) {
      setHasNumberOfCheckedError(false);
    }
  }, [numberOfChecked]);

  const [mutate, { loading, error }] = useMutation<
    UpdateMultipleChoiceQuestionOption,
    UpdateMultipleChoiceQuestionOptionVariables
  >(MUTATION);

  const [
    deleteOptionMutation,
    { loading: isDeletingOption, error: deleteOptionError },
  ] = useMutation<
    DeleteMultipleChoiceQuestionOption,
    DeleteMultipleChoiceQuestionOptionVariables
  >(DELETE_OPTION);

  const titleKey = `title-${option.id}`;

  const { register, handleSubmit, watch } =
    useForm<UpdateMultipleChoiceQuestionOptionForm>({
      defaultValues: {
        [titleKey]: option.title,
      },
    });
  const title = watch(titleKey, option.title);

  const { showConfirm, InlineConfirm } = useConfirmDialog();

  const onDeleteIconClick = () => {
    showConfirm({
      text: "Er du sikker på at du vil slette svaralternativet?",
      okButtonText: "Ja, slett",
      okButtonAction: () => deleteOption(),
      cancelButtonText: "Avbryt",
    });
  };

  const onSubmitOption: SubmitHandler<
    UpdateMultipleChoiceQuestionOptionForm
  > = () => {
    void mutate({
      variables: {
        input: {
          correct: option.correct,
          title: { updatedValue: title },
          multipleChoiceId,
          questionId,
          optionId: option.id,
        },
      },
    });
  };

  const deleteOption = async () => {
    await deleteOptionMutation({
      variables: {
        input: {
          multipleChoiceId: multipleChoiceId,
          questionId: questionId,
          optionId: option.id,
        },
      },
    });

    refetch();
  };

  const onClickOptionCorrect = (e: React.MouseEvent<HTMLInputElement>) => {
    const editDisabled = numberOfChecked === 1 && option.correct === true;

    if (editDisabled) {
      e.preventDefault();
    }

    setHasNumberOfCheckedError(editDisabled);

    if (!editDisabled) {
      void mutate({
        variables: {
          input: {
            correct: !option.correct,
            title: { updatedValue: title },
            multipleChoiceId,
            questionId,
            optionId: option.id,
          },
        },
      });

      setHasNumberOfCheckedError(false);
    }
  };

  useDebounce(title, option.title, 500, handleSubmit(onSubmitOption));

  const [ref, isDragging] = useDragAndDropElement(
    option.id,
    index,
    DnDItemType.MULTIPLE_CHOICE_QUESTION_OPTION,
    moveElement,
    saveOrder,
  );

  return (
    <ListItem
      className={optionStyles.container}
      passRef={ref}
      style={{ opacity: isDragging ? 0 : 1 }}
      onDragStart={preventDragOnTextInput}
    >
      <input
        type="checkbox"
        id={option.id}
        defaultChecked={
          type == MultipleChoiceType.STANDARD
            ? parseCorrect(option.correct)
            : true
        }
        disabled={type == MultipleChoiceType.GROUPING}
        onClick={onClickOptionCorrect}
        {...register(option.id)}
      />
      <InputField
        id={titleKey}
        kind="normal"
        placeholder="Skriv inn svaralternativ.."
        aria-label={`Alternativ: ${option.title}`}
        register={register(titleKey, {
          required: "Svaralternativ kan ikke være tomt",
          minLength: 1,
        })}
      />
      {InlineConfirm ?? (
        <>
          <img src={barsicon} alt="icon" className={optionStyles.icon} />
          <span style={{ marginLeft: "auto" }}>
            <IconButton
              icon={deleteIcon}
              altText={`Slett alternativet ${option.title}`}
              onClick={onDeleteIconClick}
            />
          </span>
        </>
      )}

      <FieldError name={titleKey} />

      <MutationLoadingOrError
        loading={loading || isDeletingOption}
        error={error || deleteOptionError}
      />
      {hasNumberOfCheckedError && (
        <SimpleErrorMessage>
          Minst ett svaralternativ må være riktig
        </SimpleErrorMessage>
      )}
    </ListItem>
  );
};
